import { VayuBlocksCssJustify,boxShadow,VayuBlocksCssborderFrame,VayuBlocksCssTextAlign,borderRadiusStyle,VayuBlocksTypography } from "@vayu-blocks/global";
import { getSvgMaskBase64 } from '@vayu-blocks/animation/maskstyle.js';

const framestyle = (attributes, view) => {
    
    const frame = {
        ...VayuBlocksCssborderFrame(attributes.frameData, view),
        ...(attributes.parentBlock === 'vayu-blocks/advance-slider' 
            ? boxShadow(attributes.advanceglobaldropshadow, view) 
            : {})
    };

    const globalsliderframe = {
        ...VayuBlocksCssborderFrame(attributes.sliderframeData, view),
        ...boxShadow(attributes.advanceglobaldropshadow, view),
    };

    let style = {
        ...(attributes.parentBlock === "vayu-blocks/advance-slider" && !attributes.borderframe
            ? globalsliderframe
            : frame),
    };
    return style;
}

const OverlayStyle = (attributes, view) => {

    const {
        blur = 0,
        brightness = 100,
        contrast = 100,
        saturation = 100,
        hue = 0,
    } = attributes.filter || {};

  // Compose backdrop-filter string
  const backdropFilterValue = `blur(${blur}px) brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) hue-rotate(${hue}deg)`;

    let style = {
        ...(attributes.overlayshow && {
            '--backgroundOverlay': attributes.overlaycolor,
        }),

        overflow: 'hidden',
        width: attributes.position?.width?.[view],
        height: attributes.position?.height?.[view],
        top: attributes.position?.top?.[view],
        left: attributes.position?.left?.[view],
        backdropFilter: backdropFilterValue,
        WebkitBackdropFilter: backdropFilterValue,
    };

    return style;
};

const CaptionStyle = (attributes, view) => {
  
    let style = {
        color: attributes.captioncolor,
        ...VayuBlocksCssTextAlign(attributes.captionalignment,view),
        ...VayuBlocksTypography(attributes.typography, view),
    };

    return style;
};

const getImageStyle = (attributes, view) => {

    const frame = {
        ...(attributes.parentBlock === 'vayu-blocks/advance-slider' && !attributes.borderframe 
            ? boxShadow(attributes.advanceglobaldropshadow, view) 
            : boxShadow(attributes.imageboxShadow, view))
    };

    return {
        ...frame,
        ...borderRadiusStyle(attributes.frameData.radius, view),
        aspectRatio: attributes.aspectRatio?.[view],
        width: attributes.imageWidth?.[view] ?? 'auto',
        height: attributes.imageHeight?.[view] ?? 'auto',

        maxWidth: '100%',
        maxHeight: '100%',

        ...((attributes.animationData?.hovereffect?.value === 'flip-front' || 
            attributes.animationData?.hovereffect?.value === 'flip-back') && {
            backfaceVisibility: 'hidden',
        }),

        ...(attributes.imagebackgroundSize && {
            objectFit: attributes.imagebackgroundSize,
        }),
        
        objectPosition: `${(attributes.focalPoint?.x ?? 0.5) * 100}% ${(attributes.focalPoint?.y ?? 0.5) * 100}%`,
        ...(attributes.duotone?.length > 1 && {
            filter: `url(#duotone-filter-${attributes.uniqueId})`,
        }),
    };
};

const getImagePositionStyle = (attributes, view) => {
    return {
        display: 'flex',
        ...VayuBlocksCssJustify(attributes.imagealignment, view),
        alignItems: 'center',
    };
};

const getInlineStyles = (attributes, view) => {
    const imagePath = getSvgMaskBase64(attributes.animationData.mask.maskshape);  

    let opacity = (attributes.showPreview || attributes.overlay) ? 1 : 0;  

    const overlayhvrcolor = attributes.overlayshow? attributes.overlayhvrcolor || attributes.overlaycolor || '': '';
    
    const framestyles = framestyle(attributes,view);

    const frameVariables = Object.entries(framestyles).reduce((acc, [key, value]) => {
        if (
            key === "borderTopWidth" ||
            key === "borderRightWidth" ||
            key === "borderBottomWidth" ||
            key === "borderLeftWidth"
        ) {
            const side = key
                .replace("border", "")     // "TopWidth"
                .replace("Width", "")      // "Top"
                .toLowerCase();            // "top"

            acc[`--wrapper-image-border-${side}`] = `${value} solid`;
        } else {
            // Default conversion for all other props
            const cssVar = `--wrapper-image-${key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`;
            acc[cssVar] = value;
        }

        return acc;
    }, {});

    const generateOverlayBorderVars = (radiusObject) => {
        if (!radiusObject) return {};

        return {
            '--overlay-border-top-left-radius': radiusObject.topLeft ?? '0px',
            '--overlay-border-top-right-radius': radiusObject.topRight?? '0px',
            '--overlay-border-bottom-left-radius': radiusObject.bottomLeft ?? '0px',
            '--overlay-border-bottom-right-radius': radiusObject.bottomRight ?? '0px',
        };
    };

    return {
        ...frameVariables,
        ...generateOverlayBorderVars(attributes.frameData?.radius?.[view]),
        ...(attributes?.animationData?.imageAnimation?.speed && { '--image-animation-spped': `${attributes.animationData?.imageAnimation?.speed}s` }),
        ...(attributes?.animationData?.hovereffect?.value && { '--image-hover-effect': `${attributes.animationData.hovereffect.value}` }),
        ...(attributes?.overlaytransitiontime != null && { '--overlay-transitiontime': `${attributes.overlaytransitiontime}s` }),
        ...(attributes?.imagetransitiontime != null && { '--image-transitiontime': `${attributes.imagetransitiontime}s` }),
        ...(opacity != null && { '--overlay-opacity': `${opacity}` }),
        ...(attributes?.buttonhvrbackground && { '--button-hvr-background': `${attributes.buttonhvrbackground}` }),
        ...(attributes?.buttonhvrcolor && { '--button-hvr-color': `${attributes.buttonhvrcolor}` }),
        ...(imagePath && { '--image-url-shape': `url(${imagePath})` }),
        ...(attributes?.animationData?.mask?.masksize && { '--image-url-size': `${attributes.animationData.mask.masksize}` }),
        ...(attributes?.animationData?.mask?.maskposition && { '--image-url-position': `${attributes.animationData.mask.maskposition}` }),
        ...(attributes?.animationData?.mask?.maskrepeat && { '--image-url-repeat': `${attributes.animationData.mask.maskrepeat}` }),
        ...((attributes.overlayshow === true && overlayhvrcolor) && { '--overlay-hvr-color': `${overlayhvrcolor}` }),
        ...(attributes?.rotation != null && { '--image-transform': `${attributes.rotation}deg` }),
        ...(attributes?.animationData?.effect?.effectColor && { '--wrapper-effect3-color': `${attributes.animationData.effect.effectColor}` }),
        ...(attributes?.overlayopacity != null && {
            '--wrapper-overlay-opacity': attributes.overlayopacity,
            '--wrapper-overlay-hover-opacity': attributes.overlayopacity,
        }),
        ...(attributes?.captionalignment && { '--wrapper-image-caption-alignment': attributes.captionalignment }),
        ...(attributes?.captionalignmentTablet && { '--wrapper-image-caption-alignment-tablet': attributes.captionalignmentTablet }),
        ...(attributes?.captionalignmentMobile && { '--wrapper-image-caption-alignment-mobile': attributes.captionalignmentMobile }),
    };
};

export {
    OverlayStyle,
    CaptionStyle,
    getImageStyle,
    getImagePositionStyle,
    getInlineStyles,
};
